import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MainTemplate } from 'templates';
import { LoadingIndicator } from 'atoms';
import {
  useAllExpenses,
  useExpenseCategories,
  useExpenseMutations,
  usePopupStateOperations,
  IExpensesFilters,
} from 'core/hooks';
import { PopupNames } from 'core';
import {
  DateRangePresets,
  IDateRangeValue,
  IExpenseFormPopupData,
  presetRange,
} from 'molecules';

import { formatMoneyCurrency } from '../_detailShared/format';
import * as S from '../FakturowniaInvoices/FakturowniaInvoices.styled';
import { ExpensesFilterBar } from './ExpensesFilterBar';
import { ExpensesTable } from './ExpensesTable';
import {
  DEFAULT_SORT,
  DEFAULT_VIEW,
  IExpensesSort,
  IExpensesView,
  TSortKey,
  computeKpis,
  filterExpenses,
  groupOptionsFromRows,
  sortExpenses,
} from './filtering';

const DEFAULT_RANGE: IDateRangeValue = {
  preset: 'thisMonth',
  ...presetRange('thisMonth'),
};

const buildDefaultFilters = (
  range: IDateRangeValue = DEFAULT_RANGE,
): IExpensesFilters => ({
  dateFrom: range.from,
  dateTo: range.to,
});

/**
 * Expenses register (TZ §3.5). State stays minimal: filters + a click-to-open
 * popup for create/edit. KPI total is summed in PLN from `amountPln` (the
 * backend-cached FX conversion) so mixed-currency pages total correctly. Rows
 * with no FX rate (`amountPln === null`) are excluded from the PLN total and
 * counted separately so we never guess a rate client-side.
 */
export const FakturowniaExpenses = memo(() => {
  const { t } = useTranslation();
  const {
    operations: { showPopup },
  } = usePopupStateOperations();

  const [range, setRange] = useState<IDateRangeValue>(DEFAULT_RANGE);
  // Backend filter = the period only; segment/search/sort/group/source/etc.
  // are applied client-side over the in-memory set (instant, no refetch).
  const [filters, setFilters] = useState<IExpensesFilters>(() =>
    buildDefaultFilters(),
  );
  const [view, setView] = useState<IExpensesView>(DEFAULT_VIEW);
  const [sort, setSort] = useState<IExpensesSort>(DEFAULT_SORT);
  const [searchInput, setSearchInput] = useState('');

  // Debounce the search box into the view (300ms — the register convention).
  useEffect(() => {
    const handle = setTimeout(() => {
      const next = searchInput.trim();
      setView((prev) =>
        prev.search === next ? prev : { ...prev, search: next },
      );
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data, loading, fetching, permissionDenied, refetch } = useAllExpenses(
    filters,
  );
  const { categories } = useExpenseCategories();
  const { setStatus } = useExpenseMutations();

  const groupOptions = useMemo(() => groupOptionsFromRows(data), [data]);
  const visibleRows = useMemo(
    () => sortExpenses(filterExpenses(data, view), sort),
    [data, view, sort],
  );
  const kpis = useMemo(() => computeKpis(data), [data]);

  const onSetStatus = useCallback(
    (id: string, status: string) => {
      setStatus(id, status).then((ok) => {
        if (ok) refetch();
      });
    },
    [setStatus, refetch],
  );

  const onRangeChange = useCallback((next: IDateRangeValue) => {
    setRange(next);
    setFilters((prev) => ({ ...prev, dateFrom: next.from, dateTo: next.to }));
  }, []);

  const onReset = useCallback(() => {
    setRange(DEFAULT_RANGE);
    setFilters(buildDefaultFilters());
    setView(DEFAULT_VIEW);
    setSort(DEFAULT_SORT);
    setSearchInput('');
  }, []);

  const onSort = useCallback((key: TSortKey) => {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      // Date/amount default to descending (newest / biggest first); text asc.
      return { key, dir: key === 'date' || key === 'amount' ? 'desc' : 'asc' };
    });
  }, []);

  const onAdd = useCallback(() => {
    const payload: IExpenseFormPopupData = { onSaved: refetch };
    showPopup(PopupNames.expenseForm, payload);
  }, [showPopup, refetch]);

  const onRowClick = useCallback(
    (expenseId: string) => {
      const payload: IExpenseFormPopupData = { expenseId, onSaved: refetch };
      showPopup(PopupNames.expenseForm, payload);
    },
    [showPopup, refetch],
  );

  if (permissionDenied) {
    return (
      <MainTemplate>
        <S.Wrapper>
          <S.PageTitle>{t('pages.fakturownia.expenses.title')}</S.PageTitle>
          <S.EmptyState>
            {t('pages.fakturownia.expenses.noAccess')}
          </S.EmptyState>
        </S.Wrapper>
      </MainTemplate>
    );
  }

  if (loading && data.length === 0) {
    return (
      <MainTemplate>
        <S.Wrapper>
          <S.CenterLoader>
            <LoadingIndicator />
          </S.CenterLoader>
        </S.Wrapper>
      </MainTemplate>
    );
  }

  const isEmpty = data.length === 0;

  return (
    <MainTemplate>
      <S.Wrapper>
        <S.HeaderRow>
          <S.PageTitle>{t('pages.fakturownia.expenses.title')}</S.PageTitle>
          <S.HeaderRight>
            <KpiPill>
              {t('pages.fakturownia.expenses.kpiTotal')}:{' '}
              <strong>{formatMoneyCurrency(kpis.total, 'PLN')}</strong>
            </KpiPill>
            <KpiPill tone="amber">
              {t('pages.fakturownia.expenses.kpiToPay')}:{' '}
              <strong>{formatMoneyCurrency(kpis.toPay, 'PLN')}</strong>
            </KpiPill>
            <KpiPill tone="green">
              {t('pages.fakturownia.expenses.kpiPaid')}:{' '}
              <strong>{formatMoneyCurrency(kpis.paid, 'PLN')}</strong>
            </KpiPill>
            {kpis.missing > 0 ? (
              <span
                title={t('pages.fakturownia.expenses.kpiUnconvertedHint')}
                style={{
                  color: '#b45309',
                  fontWeight: 600,
                  fontSize: 12,
                  alignSelf: 'center',
                }}
              >
                {t('pages.fakturownia.expenses.kpiUnconverted', {
                  count: kpis.missing,
                })}
              </span>
            ) : null}
            <AddButton type="button" onClick={onAdd}>
              {t('pages.fakturownia.expenses.addNew')}
            </AddButton>
          </S.HeaderRight>
        </S.HeaderRow>
        <S.SubTitle>{t('pages.fakturownia.expenses.subtitle')}</S.SubTitle>

        <DateRangePresets value={range} onChange={onRangeChange} />

        <ExpensesFilterBar
          view={view}
          onChange={setView}
          onReset={onReset}
          categories={categories}
          groupOptions={groupOptions}
          searchInput={searchInput}
          onSearchInput={setSearchInput}
        />

        {isEmpty ? (
          <S.EmptyState>{t('pages.fakturownia.expenses.empty')}</S.EmptyState>
        ) : (
          <>
            {visibleRows.length !== data.length ? (
              <S.SubTitle>
                {t('pages.fakturownia.expenses.shown', {
                  shown: visibleRows.length,
                  total: data.length,
                })}
              </S.SubTitle>
            ) : null}
            <S.DimWhenFetching fetching={fetching}>
              {visibleRows.length === 0 ? (
                <S.EmptyState>
                  {t('pages.fakturownia.expenses.noResults')}
                </S.EmptyState>
              ) : (
                <ExpensesTable
                  rows={visibleRows}
                  onRowClick={onRowClick}
                  onSetStatus={onSetStatus}
                  sort={sort}
                  onSort={onSort}
                />
              )}
            </S.DimWhenFetching>
          </>
        )}
      </S.Wrapper>
    </MainTemplate>
  );
});

const KPI_TONES = {
  default: { bg: '#f1f5f9', fg: '#475569' },
  amber: { bg: '#fffbeb', fg: '#b45309' },
  green: { bg: '#ecfdf5', fg: '#047857' },
};

const KpiPill = ({
  children,
  tone = 'default',
}: {
  children: React.ReactNode;
  tone?: keyof typeof KPI_TONES;
}) => (
  <span
    style={{
      display: 'inline-block',
      padding: '6px 12px',
      borderRadius: 999,
      background: KPI_TONES[tone].bg,
      color: KPI_TONES[tone].fg,
      fontSize: 12,
      fontWeight: 600,
    }}
  >
    {children}
  </span>
);

const AddButton = (props: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    {...props}
    style={{
      border: '1px solid #2563eb',
      background: '#2563eb',
      color: '#fff',
      padding: '6px 14px',
      borderRadius: 8,
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer',
    }}
  />
);
